import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createZone, deleteZone, getZones, updateZone, ZonePayload } from '@/api/zonesService';
import { queryKeys } from '@/lib/queryKeys';
import { toaster } from '@/components/ui/toaster';

const errorMessage = (error: unknown) => {
  if (typeof error === 'object' && error !== null) {
    const res = (error as { response?: { data?: { message?: string } } }).response;
    if (res?.data?.message) return res.data.message;
  }
  return error instanceof Error ? error.message : 'Une erreur est survenue';
};

export const useZones = (filters: { search?: string; sport?: string }) => {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin', 'pv2', 'zones'] });

  const { data: zones = [], isLoading } = useQuery({
    queryKey: queryKeys.pv2Zones(filters),
    queryFn: () => getZones(filters),
  });

  const createMutation = useMutation({
    mutationFn: (payload: ZonePayload) => createZone(payload),
    onSuccess: () => {
      invalidate();
      toaster.create({ title: 'Zone créée', type: 'success' });
    },
    onError: (error) => toaster.create({ title: 'Erreur', description: errorMessage(error), type: 'error' }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: ZonePayload }) => updateZone(id, payload),
    onSuccess: () => {
      invalidate();
      toaster.create({ title: 'Zone mise à jour', type: 'success' });
    },
    onError: (error) => toaster.create({ title: 'Erreur', description: errorMessage(error), type: 'error' }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteZone(id),
    onSuccess: () => {
      invalidate();
      toaster.create({ title: 'Zone supprimée', type: 'success' });
    },
    onError: (error) => toaster.create({ title: 'Suppression impossible', description: errorMessage(error), type: 'error' }),
  });

  return {
    zones,
    isLoading,
    createZone: createMutation.mutateAsync,
    updateZone: updateMutation.mutateAsync,
    deleteZone: deleteMutation.mutateAsync,
    isSaving: createMutation.isPending || updateMutation.isPending,
    invalidate,
  };
};
